/**
 * 課題管理の画面で、次にやること（判定表 BR-19。API が決めて返す）と待ちの様子を言葉と押せる操作にする。
 *
 * 判定表の当て方は API だけが持ち、画面は返ってきた種類（kind）を文言・カードの問い・大ボタンの行き先に写すだけ。
 * 重大度は画面に出さず、期限切れを赤で示す。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の次にやることを一覧・詳細・やることのカードの文言と、詳細の大ボタンの行き先（カードへ移る・小窓を開く・その場で記録）にする
 * @graph-connects none
 */
import type {
  CaseAttention,
  IssueNextAction,
  IssueNextActionKind,
  IssueWaiting,
  NextActionMissingField,
  TodoCard,
  TodoCardType,
} from '@cortex/domain-ac-mens-hub-api';

import type { Translate } from './issue-mgmt-values.js';

/**
 * 次にやることの短い文言（一覧の列・詳細の箱）のキー。案件を確かめる行は期限超過と要確認で書き分ける。
 * @graph-connects none
 */
export const NEXT_ACTION_LABEL_KEYS: Record<IssueNextActionKind | 'check_overdue', string> = {
  decide: 'issueMgmt.next.decide',
  fill: 'issueMgmt.next.fillRequired',
  redecide: 'issueMgmt.next.redecide',
  check_case: 'issueMgmt.next.checkCase',
  check_overdue: 'issueMgmt.next.checkOverdue',
  link_case: 'issueMgmt.next.linkCase',
  verify_fix: 'issueMgmt.next.verifyFix',
  reply: 'issueMgmt.next.reply',
  start_check: 'issueMgmt.next.startCheck',
};

/**
 * 次にやることの短い文言。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の次にやることを、一覧の列と詳細の箱に出す短い文言にする
 * @graph-connects NEXT_ACTION_LABEL_KEYS [references] 文言のキー
 */
export function nextActionLabel(next: IssueNextAction, t: Translate): string {
  if (next.kind === 'check_case' && next.attention === 'overdue') {
    return t(NEXT_ACTION_LABEL_KEYS.check_overdue);
  }
  return t(NEXT_ACTION_LABEL_KEYS[next.kind]);
}

/**
 * 待ちの様子の文言（Backlog の進み待ち・担当が対応中・見直す日）。待ちが無ければ「いまやることはありません」。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 次にやることが無い課題の待ちの様子（Backlog の進み・担当の対応中・見直す日）を文言にする
 * @graph-connects none
 */
export function waitingLabel(
  waiting: IssueWaiting | null,
  t: Translate,
  formatDate: (date: string) => string,
): string {
  if (waiting === null) return t('issueMgmt.next.none');
  switch (waiting.kind) {
    case 'cases':
      return t('issueMgmt.next.waitingCases', {
        total: waiting.total,
        completed: waiting.completed,
      });
    case 'working':
      return waiting.assigneeName === null
        ? t('issueMgmt.next.working')
        : t('issueMgmt.next.inProgressBy', { name: waiting.assigneeName });
    case 'review_on':
      return t('issueMgmt.next.reviewOn', { date: formatDate(waiting.date) });
    default: {
      const unreachable: never = waiting;
      throw new Error(`unknown waiting: ${JSON.stringify(unreachable)}`);
    }
  }
}

/**
 * 詳細の大ボタンを押したときの行き先。
 * - todo: やることの該当カードへ移る（判定・直ったかは中身と声を見て決めるため）
 * - decide: 判定の小窓（対応するを今の値つきで開く）
 * - link_case: 案件を紐づける小窓
 * - done: 対応したの小窓
 * - reply・check_case: その場で記録する（トーストで元に戻せる）
 * @graph-connects none
 */
export type NextActionTarget = 'todo' | 'decide' | 'link_case' | 'done' | 'reply' | 'check_case';

/**
 * 大ボタンの文言キーと行き先。
 * @graph-connects none
 */
const NEXT_ACTION_BUTTONS: Record<
  IssueNextActionKind,
  { labelKey: string; target: NextActionTarget }
> = {
  decide: { labelKey: 'issueMgmt.next.buttonDecide', target: 'todo' },
  redecide: { labelKey: 'issueMgmt.next.buttonDecide', target: 'todo' },
  fill: { labelKey: 'issueMgmt.next.fillRequired', target: 'decide' },
  link_case: { labelKey: 'issueMgmt.next.buttonLinkCase', target: 'link_case' },
  verify_fix: { labelKey: 'issueMgmt.next.verifyFix', target: 'todo' },
  reply: { labelKey: 'issueMgmt.next.buttonReplied', target: 'reply' },
  check_case: { labelKey: 'issueMgmt.next.buttonChecked', target: 'check_case' },
  start_check: { labelKey: 'issueMgmt.next.markDone', target: 'done' },
};

/**
 * 詳細の大ボタン（文言と行き先）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題詳細の次にやることの大ボタンの文言と、押したときの行き先（カードへ移る・小窓・その場で記録）を決める
 * @graph-connects NEXT_ACTION_BUTTONS [references] 大ボタンの対応表
 */
export function nextActionButton(
  next: IssueNextAction,
  t: Translate,
): { label: string; target: NextActionTarget } {
  const button = NEXT_ACTION_BUTTONS[next.kind];
  return { label: t(button.labelKey), target: button.target };
}

/**
 * 案件を確かめてもらう理由の、カードの問いの文言キー。
 * @graph-connects none
 */
export const CASE_ATTENTION_QUESTION_KEYS: Record<CaseAttention, string> = {
  not_found: 'issueMgmt.card.checkNotFound',
  deleted: 'issueMgmt.card.checkDeleted',
  on_hold: 'issueMgmt.card.checkOnHold',
  unknown_status: 'issueMgmt.card.checkUnknown',
  missing_status: 'issueMgmt.card.checkUnknown',
  overdue: 'issueMgmt.card.checkOverdue',
};

/**
 * 案件を確かめてもらう理由の、説明の1文の文言キー。
 * @graph-connects none
 */
export const CASE_ATTENTION_REASON_KEYS: Record<CaseAttention, string> = {
  not_found: 'issueMgmt.cases.reasonNotFound',
  deleted: 'issueMgmt.cases.reasonDeleted',
  on_hold: 'issueMgmt.cases.reasonOnHold',
  unknown_status: 'issueMgmt.cases.reasonUnknown',
  missing_status: 'issueMgmt.cases.reasonMissing',
  overdue: 'issueMgmt.cases.reasonOverdue',
};

/**
 * やることのカードの問い（課題のタイトルで書く）。振り分けは声の要点そのもの。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることのカードの問い（『タイトル』に対応する？・起案して紐づけて・直りましたか？など）を課題のタイトルで書く
 * @graph-connects CASE_ATTENTION_QUESTION_KEYS [references] 案件を確かめる問い
 */
export function cardQuestion(card: TodoCard, t: Translate, devRequired?: boolean | null): string {
  if (card.source === 'triage') return card.point.title;
  const title = card.title;
  const next = card.next;
  switch (next.kind) {
    case 'decide':
    case 'redecide':
      return t('issueMgmt.card.decideQuestion', { title });
    case 'fill':
      return t('issueMgmt.card.fillQuestion', { title });
    case 'link_case':
      return t('issueMgmt.card.linkQuestion', { title });
    case 'check_case':
      return t(CASE_ATTENTION_QUESTION_KEYS[next.attention], { title });
    case 'verify_fix':
      return devRequired === false
        ? t('issueMgmt.card.verifyQuestionNoDev', { title })
        : t('issueMgmt.card.verifyQuestion', { title });
    case 'reply':
      return t('issueMgmt.card.replyQuestion', { title });
    case 'start_check':
      return t('issueMgmt.card.startQuestion', { title });
    default: {
      const unreachable: never = next;
      throw new Error(`unknown next action: ${JSON.stringify(unreachable)}`);
    }
  }
}

/**
 * カードの種類の文言キー（振り分け・判定・確認）。
 * @graph-connects none
 */
export const TODO_TYPE_LABEL_KEYS: Record<TodoCardType, string> = {
  triage: 'issueMgmt.todo.typeTriage',
  judge: 'issueMgmt.todo.typeJudge',
  check: 'issueMgmt.todo.typeCheck',
};

/**
 * 「対応する」の必須項目で欠けているものの文言キー（項目の名前）。
 * @graph-connects none
 */
export const MISSING_FIELD_LABEL_KEYS: Record<NextActionMissingField, string> = {
  assignee: 'issueMgmt.field.assignee',
  priority: 'issueMgmt.field.priority',
  devRequired: 'issueMgmt.field.devRequired',
};
